import { useCallback, useSyncExternalStore } from 'react';
import { Query, useQueryClient } from '@tanstack/react-query';

/**
 * מה שרענון האזור האישי מושך מחדש (HAT-1237).
 *
 * המדווח ביקש "רענון וטעינה מחדש של האזור האישי", כלומר את מה שטעינה מחדש של
 * הדף הייתה עושה - לא את הלוח הזה או האחר. לכן הכפתור יושב בפס הקרן, מעל כל
 * מסך, ולא בסרגל של לוח מסוים.
 *
 * ⚠️ ההצטרפות מפורשת, ולא "הכל חוץ מ-". רענון גורף היה מושך גם נקודות קצה
 * שעולות כסף אצל ספק חיצוני: הרשימה של הוראות הקבע קוראת ל-GetKevaJson של
 * נדרים, שמוגבל לעשרים קריאות בשעה למוסד ומשותף לכל הקרנות שתחתיו, ואותה
 * קריאה עצמה יושבת גם ב"תמונת מצב" (ר' useDashboardData) - מסך שאיש אינו
 * חושב עליו כשהוא מוסיף כפתור רענון.
 *
 * בין שתי הדרכים לטעות, זו הבטוחה: מי ששוכח להצהיר מקבל מסך בלי רענון, וזה
 * חוסר שמבחינים בו ומתקנים. מי שהיה שוכח לפטור היה שורף מכסה משותפת של מוסד
 * שלם, וזה נכשל אצל אחרים ובשקט.
 */
export interface RefreshableQueryMeta extends Record<string, unknown> {
  /** מסמן שהשאילתה היא חלק מ"הנתונים של המסך" ולכן נמשכת מחדש ברענון. */
  refreshable: true;
}

const isRefreshable = (query: Query): boolean => query.meta?.refreshable === true;

/**
 * שאילתה שכבויה אינה חלק מהמסך.
 *
 * `enabled: !!url` משאיר במטמון שאילתות שממתינות לתנאי שלא התקיים - העמודה
 * של תרומות אופליין בקרן שהדגל שלה כבוי, למשל. הן יושבות ב-pending לנצח בלי
 * שאיש מחכה להן, ובלי ההבחנה הזו הן היו מסתירות את החותמת בכל מסך שיש בו אחת.
 */
const isEngaged = (query: Query): boolean => !(query.state.status === 'pending' && query.state.fetchStatus === 'idle');

/** Only what is on screen right now: a query nobody is observing is not this page's data. */
const activeRefreshableQueries = (queries: Query[]): Query[] => queries.filter((query) => isRefreshable(query) && query.getObserversCount() > 0);

export interface GlobalRefreshState {
  /** מושך מחדש את כל מה שהמסך הנוכחי הצהיר עליו. false אם משהו נכשל. */
  refresh: () => Promise<boolean>;
  /** משיכה באוויר כרגע. */
  isRefreshing: boolean;
  /**
   * הישן מבין הנתונים שעל המסך, ולא החדש שבהם: החותמת אומרת "שום דבר כאן אינו
   * ישן מזה", וזו התשובה לשאלה ששואלים כשמסתכלים עליה. 0 לפני המשיכה הראשונה.
   */
  updatedAt: number;
  /** האם יש בכלל מה לרענן במסך הזה. כשאין, הכפתור לא מוצג. */
  hasRefreshable: boolean;
}

/**
 * רענון האזור האישי: מושך מחדש את הנתונים של המסך הנוכחי בלי לטעון את הדף.
 *
 * טעינה מחדש של הדף עוברת דרך שחזור הסשן מהאחסון, ולכן סשן שפג או ששכב ללא
 * פעילות מסתיים שם (ר' sessionTimeout), וכל 401 בודד מנקה את האחסון ומפנה
 * ל-login (ר' ApiClient). משיכה מכאן אינה נוגעת בסשן, והלחיצה עצמה נספרת
 * כפעילות ולכן גם מאריכה אותו.
 */
export const useGlobalRefresh = (): GlobalRefreshState => {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryCache();

  /*
   * המצב נקרא מהמטמון עצמו ולא מ-state מקבילי: כל שאילתה שמצטרפת למסך או
   * עוזבת אותו משנה את התשובה, ושכפול שלה ל-state היה יוצא מסנכרון בדיוק
   * כשהמסך מתחלף. useSyncExternalStore קורא מהמקור בכל רינדור.
   */
  const snapshot = useSyncExternalStore(
    useCallback((onChange) => cache.subscribe(onChange), [cache]),
    useCallback(() => {
      const queries = activeRefreshableQueries(cache.getAll());
      if (queries.length === 0) return '0|0|0';

      const isRefreshing = queries.some((query) => query.state.fetchStatus === 'fetching');

      /*
       * ⚠️ אין חותמת כל עוד משהו על המסך טרם חזר.
       *
       * החותמת נקראת כ"זה מה שאתה רואה, וזה גילו", ולכן היא אסורה בזמן שהתוכן
       * עדיין נטען: היתרה חוזרת מהר מלוח התנועות, ובטעינה הראשונה הופיעה שעה
       * ליד טבלה ריקה - הבטחה שהנתונים כאן בזמן שהם עוד בדרך. עדיף בלי שעה
       * מאשר שעה שאינה מתארת את מה שעל המסך.
       */
      const engaged = queries.filter(isEngaged);
      const stamps = engaged.map((query) => query.state.dataUpdatedAt);
      const oldest = stamps.length > 0 && stamps.every((stamp) => stamp > 0) ? Math.min(...stamps) : 0;

      // מחרוזת ולא אובייקט: useSyncExternalStore משווה בזהות, ואובייקט טרי
      // בכל קריאה היה מרנדר בלולאה אינסופית.
      return `${queries.length}|${isRefreshing ? 1 : 0}|${oldest}`;
    }, [cache]),
    () => '0|0|0'
  );

  const [count, refreshing, oldest] = snapshot.split('|').map(Number);

  const refresh = useCallback(async () => {
    await queryClient.refetchQueries({ predicate: isRefreshable, type: 'active' });

    // נקרא אחרי המשיכה ולא לפניה: מה שמעניין הוא אם המסך שלפנינו חזר, ולא
    // מה היה מצבו קודם.
    return !activeRefreshableQueries(cache.getAll()).some((query) => query.state.status === 'error');
  }, [queryClient, cache]);

  return { refresh, isRefreshing: refreshing === 1, updatedAt: oldest, hasRefreshable: count > 0 };
};

export default useGlobalRefresh;
